import {
  ApiResponse,
  ProviderUser,
  PaginationQuery,
  PaginationResponse,
} from '@magnetar/admin-api-interfaces'
import { useAuth } from './useAuth'
import { useGet } from './useFetch'

interface UseProviderUsersParams extends PaginationQuery {
  provider_id?: number
  account?: string
}

export function useProviderUsers({
  page = 1,
  pageSize = 10,
  provider_id,
  account,
}: UseProviderUsersParams) {
  const { token } = useAuth()

  const queryParams = new URLSearchParams()
  queryParams.append('page', String(page))
  queryParams.append('pageSize', String(pageSize))
  if (provider_id) queryParams.append('provider_id', String(provider_id))
  if (account) queryParams.append('account', account)

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<ProviderUser>>>(
    token ? `/api/provider-users?${queryParams.toString()}` : null,
    undefined,
    token ?? '',
  )

  return {
    providerUsers: data?.data?.items,
    pagination: data?.data
      ? {
          page: data.data.page,
          pageSize: data.data.pageSize,
          total: data.data.total,
          totalPages: data.data.totalPages,
        }
      : undefined,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

export function useProviderUser(id: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<ProviderUser>>(
    token && id ? `/api/provider-users/${id}` : null,
    undefined,
    token ?? '',
  )

  return {
    providerUser: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get provider user permissions
export function useProviderUserPermissions(providerId: number | null, providerUserId: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<{
    module: string
    allowed_action: string[]
  }[]>>(
    token && providerId && providerUserId
      ? `/api/providers/${providerId}/users/${providerUserId}/permissions`
      : null,
    undefined,
    token ?? '',
  )

  return {
    permissions: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
